import { Controller } from "@hotwired/stimulus"

// Lightbox with zoom + pan.
// - Defaults to a sensible fit: fills ~85% of viewport on the constraining axis.
// - Pixel art (natural ≤ 200px) snaps to integer multiples and renders pixelated.
// - Scroll wheel + / - keys and +/- buttons zoom; 0 or Fit button resets.
// - Click-and-drag pans once zoomed past the fit.
export default class extends Controller {
  static targets = ["overlay", "image", "caption", "scaleLabel", "counter", "prev", "next"]

  connect() {
    this.keydownHandler = this.handleKeydown.bind(this)
    this.state = {
      scale: 1,
      baseScale: 1,
      offsetX: 0,
      offsetY: 0,
      isPanning: false,
      panStartX: 0,
      panStartY: 0,
      isPixelArt: false,
      items: [],
      index: 0
    }
  }

  open(event) {
    event.preventDefault()
    const trigger = event.currentTarget
    if (!this.hasOverlayTarget || !this.hasImageTarget) return

    this.state.items = Array.from(this.element.querySelectorAll("[data-lightbox-full]"))
    this.state.index = Math.max(0, this.state.items.indexOf(trigger))

    this.overlayTarget.classList.remove("hidden")
    document.addEventListener("keydown", this.keydownHandler)
    document.body.style.overflow = "hidden"

    this.showCurrent()
  }

  showCurrent() {
    const item = this.state.items[this.state.index]
    if (!item) return
    const fullSrc = item.dataset.lightboxFull
    const caption = item.dataset.lightboxCaption || ""

    this.imageTarget.src = fullSrc
    this.imageTarget.alt = caption
    this.imageTarget.onload = () => this.fit()

    if (this.hasCaptionTarget) {
      this.captionTarget.textContent = caption
      this.captionTarget.classList.toggle("hidden", caption.length === 0)
    }
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${this.state.index + 1} / ${this.state.items.length}`
    }
    const multiple = this.state.items.length > 1
    if (this.hasPrevTarget) this.prevTarget.classList.toggle("hidden", !multiple)
    if (this.hasNextTarget) this.nextTarget.classList.toggle("hidden", !multiple)
  }

  next(event) {
    if (event) event.preventDefault()
    if (this.state.items.length <= 1) return
    this.state.index = (this.state.index + 1) % this.state.items.length
    this.showCurrent()
  }

  prev(event) {
    if (event) event.preventDefault()
    if (this.state.items.length <= 1) return
    this.state.index = (this.state.index - 1 + this.state.items.length) % this.state.items.length
    this.showCurrent()
  }

  close() {
    if (!this.hasOverlayTarget) return
    this.overlayTarget.classList.add("hidden")
    document.removeEventListener("keydown", this.keydownHandler)
    document.body.style.overflow = ""
  }

  fit() {
    const img = this.imageTarget
    const nw = img.naturalWidth || 1
    const nh = img.naturalHeight || 1

    this.state.isPixelArt = nw <= 200 && nh <= 200
    img.style.imageRendering = this.state.isPixelArt ? "pixelated" : "auto"

    const maxW = window.innerWidth * 0.85
    const maxH = window.innerHeight * 0.80
    let scale = Math.min(maxW / nw, maxH / nh)
    if (this.state.isPixelArt) scale = Math.max(1, Math.floor(scale))

    this.state.baseScale = scale
    this.state.scale = scale
    this.state.offsetX = 0
    this.state.offsetY = 0
    this.applyTransform()
  }

  applyTransform() {
    const { scale, offsetX, offsetY } = this.state
    this.imageTarget.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(${scale})`
    this.imageTarget.style.transformOrigin = "center center"
    this.imageTarget.style.cursor = scale > this.state.baseScale ? (this.state.isPanning ? "grabbing" : "grab") : "default"
    if (this.hasScaleLabelTarget) {
      this.scaleLabelTarget.textContent = `${Math.round(scale * 100)}%`
    }
  }

  zoomIn(event)    { if (event) event.preventDefault(); this.setScale(this.state.scale * 1.25) }
  zoomOut(event)   { if (event) event.preventDefault(); this.setScale(this.state.scale / 1.25) }
  zoomReset(event) { if (event) event.preventDefault(); this.fit() }

  setScale(next) {
    const min = 0.1
    const max = this.state.isPixelArt ? 64 : 8
    this.state.scale = Math.max(min, Math.min(max, next))
    this.applyTransform()
  }

  wheelZoom(event) {
    if (!this.hasOverlayTarget || this.overlayTarget.classList.contains("hidden")) return
    event.preventDefault()
    const delta = event.deltaY > 0 ? 1 / 1.15 : 1.15
    this.setScale(this.state.scale * delta)
  }

  handleKeydown(event) {
    switch (event.key) {
      case "Escape":           this.close();      break
      case "+": case "=":      this.zoomIn();     break
      case "-": case "_":      this.zoomOut();    break
      case "0":                this.zoomReset();  break
      case "ArrowRight":       this.next();       break
      case "ArrowLeft":        this.prev();       break
    }
  }

  startPan(event) {
    if (this.state.scale <= this.state.baseScale) return
    event.preventDefault()
    this.state.isPanning = true
    this.state.panStartX = event.clientX - this.state.offsetX
    this.state.panStartY = event.clientY - this.state.offsetY
    this.applyTransform()
  }

  pan(event) {
    if (!this.state.isPanning) return
    this.state.offsetX = event.clientX - this.state.panStartX
    this.state.offsetY = event.clientY - this.state.panStartY
    this.applyTransform()
  }

  endPan() {
    if (!this.state.isPanning) return
    this.state.isPanning = false
    this.applyTransform()
  }

  disconnect() {
    document.removeEventListener("keydown", this.keydownHandler)
    document.body.style.overflow = ""
  }
}
